import { useState, useCallback } from 'react';
import { validateField } from './utils';

const useFormData = <T>(initialFormData?: T | null) => {
  const [formData, setFormData] = useState<T>(initialFormData || ({} as T));
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleCheckbox = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.persist();
      setFormData((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.checked,
      }));
    },
    [setFormData],
  );

  const handleErrors = useCallback(
    (error: Record<string, string>) => {
      setFormErrors((prevState) => ({
        ...prevState,
        ...error,
      }));
    },
    [setFormErrors],
  );

  const removeFormError = useCallback(
    (fieldName: string) => {
      setFormErrors((prevState) => {
        const newState = { ...prevState };
        delete newState[fieldName];
        return newState;
      });
    },
    [setFormErrors],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      e.persist();

      if (!value.trim()) {
        handleErrors({ [name]: 'This is a required field.' });
      } else {
        removeFormError(name);
      }

      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [removeFormError, handleErrors, setFormData],
  );

  const resetField = useCallback(
    (fieldName: string) => {
      setFormData((prevState) => ({
        ...prevState,
        [fieldName]: '',
      }));
    },
    [setFormData],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const error = validateField(name, value);
      if (error) {
        handleErrors({ [name]: error });
      }
    },
    [handleErrors],
  );

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    removeFormError('postal_code');
    const postalCode = (formData as T & { postal_code: string }).postal_code;

    if (!postalCode?.trim()) {
      handleErrors({ postal_code: 'This is a required field.' });
    } else {
      removeFormError('postal_code');
    }

    setFormData({ ...formData, country: e.target.value });
  };

  return {
    handleInputChange,
    handleCheckbox,
    handleErrors,
    resetField,
    setFormData,
    setFormErrors,
    formData,
    formErrors,
    removeFormError,
    validateField,
    handleBlur,
    handleCountryChange,
  };
};

export default useFormData;
